import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  Box,
  Typography,
  Button,
  IconButton,
  Alert,
  useTheme,
} from "@mui/material";
import {
  Security as SecurityIcon,
  Add as AddIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import { SourceTitle } from "../../../../../../utils/enums";

const AddRiskChoiceDialog = ({ open, onClose, onSelectOption }) => {
  const theme = useTheme();
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          fontWeight: "bold",
          fontSize: "16px",
          color: theme.customColors.text.strong,
          background: theme.customColors.background.cardHeader,
          borderBottom: `1px solid ${theme.customColors.border.main}`,
        }}
      >
        Add New Risk
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          }}
        >
          <ClearIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{ background: theme.customColors.background.main }}
      >
        <Alert
          severity="info"
          sx={{
            mb: 3,
            backgroundColor: theme.customColors.background.info,
            border: `1px solid ${theme.customColors.border.accent}`,
          }}
        >
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ fontSize: "14px", color: theme.customColors.text.strong }}
          >
            Choose how you would like to add a new risk to your register.
          </Typography>
        </Alert>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                cursor: "pointer",
                border: `1px solid ${theme.customColors.border.main}`,
                borderRadius: "12px",
                background: theme.customColors.background.main,
                boxShadow: theme.customColors.shadow.main,
                transition: "all 0.2s ease",
                height: "100%",
                "&:hover": {
                  borderColor: theme.palette._blue.main,
                  backgroundColor: theme.customColors.background.hover,
                  transform: "translateY(-2px)",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                },
              }}
              onClick={() => onSelectOption(SourceTitle.LIBRARY)}
            >
              <CardContent
                sx={{
                  p: 2,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  pb: 1,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    mb: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "10px",
                      backgroundColor: theme.palette._blue.light,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <SecurityIcon
                      sx={{ fontSize: 20, color: theme.palette._blue.main }}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="h5"
                      fontWeight={700}
                      sx={{
                        fontSize: "15px",
                        color: theme.customColors.text.strong,
                        mb: 0.5,
                      }}
                    >
                      Add from Library
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: "12px", lineHeight: 1.3 }}
                    >
                      Select from our comprehensive library of pre-defined risks
                      with standard assessments and controls.
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card
              sx={{
                cursor: "pointer",
                border: `1px solid ${theme.customColors.border.main}`,
                borderRadius: "12px",
                background: theme.customColors.background.main,
                boxShadow: theme.customColors.shadow.main,
                transition: "all 0.2s ease",
                height: "100%",
                "&:hover": {
                  borderColor: theme.palette.warning.main,
                  backgroundColor: theme.customColors.background.hover,
                  transform: "translateY(-2px)",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                },
              }}
              onClick={() => onSelectOption(SourceTitle.MANUAL)}
            >
              <CardContent
                sx={{
                  p: 2,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  pb: 1,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    mb: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "10px",
                      backgroundColor: theme.customColors.status.warning.bg,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <AddIcon
                      sx={{
                        fontSize: 20,
                        color: theme.customColors.status.warning.main,
                      }}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="h5"
                      fontWeight={700}
                      sx={{
                        fontSize: "15px",
                        color: theme.customColors.text.strong,
                        mb: 0.5,
                      }}
                    >
                      Create Manually
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: "12px", lineHeight: 1.3 }}
                    >
                      Create a custom risk with your own assessment criteria and
                      specific requirements.
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions
        sx={{
          background: theme.customColors.background.cardHeader,
          borderTop: `1px solid ${theme.customColors.border.main}`,
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            fontSize: "14px",
            color: theme.customColors.text.secondary,
            borderColor: theme.customColors.border.main,
            "&:hover": {
              backgroundColor: theme.customColors.background.hover,
              color: theme.customColors.text.strong,
            },
          }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddRiskChoiceDialog;
